<template>
  <Modal v-bind="$attrs" v-on="$listeners" @closed="closed">
    <template #title>社員選択</template>
    <template #subtitle>
      <span class="font-bold">選択した社員それぞれに、同じ内容のメインタスクを登録します</span>
    </template>

    <div class="text-sm" style="width: 44rem; max-width: 100%">

      <!-- 社員名 検索 -->
      <div class="mb-3">
        <div class="text-xs font-bold text-gray-700 mb-1">社員名</div>
        <div class="flex gap-2">
          <input
            type="text"
            v-model="keyword"
            class="flex-1 rounded border-gray-300 text-sm text-gray-700"
            @keydown.enter.prevent="search"
          />
          <WhiteButton text="検索" size="normal" @click="search" />
        </div>
      </div>

      <!-- 部署 検索（複数選択可・兼任部署でもヒット） -->
      <div class="mb-3">
        <div class="text-xs font-bold text-gray-700 mb-1">部署（複数選択可）</div>
        <div class="flex flex-wrap gap-2">
          <div
            v-for="dep in departmentList"
            :key="'dep' + dep.departmentId"
            @click="toggleDepartment(dep.departmentId)"
            class="flex items-center gap-1 px-2 py-1 border rounded cursor-pointer"
            :class="selectedDepartmentIds.includes(dep.departmentId) ? 'bg-indigo-50 border-indigo-500 text-indigo-700 font-bold' : 'border-gray-300 text-gray-700'"
          >
            <input
              type="checkbox"
              :checked="selectedDepartmentIds.includes(dep.departmentId)"
              class="pointer-events-none h-4 w-4 text-indigo-600 border-gray-300 rounded"
            />
            {{ dep.name }}
          </div>
        </div>
      </div>

      <!-- すべて選択 / 件数 -->
      <div class="flex items-center border-t border-b py-2 px-2 bg-gray-50">
        <div @click="toggleAll" class="flex items-center gap-2 cursor-pointer text-gray-700">
          <input
            type="checkbox"
            :checked="isAllChecked"
            class="pointer-events-none h-4 w-4 text-indigo-600 border-gray-300 rounded"
          />
          すべて選択
        </div>
        <div class="ml-auto text-gray-600">
          <span class="font-bold text-indigo-700">{{ checkedUserIds.length }}</span> 件選択中 / 全 {{ filteredUsers.length }} 件
        </div>
      </div>

      <!-- 一覧 -->
      <div class="overflow-y-auto" style="max-height: 22rem">
        <table class="w-full">
          <thead>
            <tr class="text-gray-500 text-xs">
              <th class="w-10"></th>
              <th class="text-left font-normal py-1">社員名</th>
              <th class="text-left font-normal py-1">部署 / 兼任部署</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="user in filteredUsers"
              :key="'user' + user.userId"
              @click="toggleUser(user.userId)"
              class="border-t cursor-pointer hover:bg-gray-50"
              :class="checkedUserIds.includes(user.userId) ? 'bg-indigo-50' : ''"
            >
              <td class="py-2 text-center">
                <input
                  type="checkbox"
                  :checked="checkedUserIds.includes(user.userId)"
                  class="pointer-events-none h-4 w-4 text-indigo-600 border-gray-300 rounded"
                />
              </td>
              <td class="py-2 font-bold text-gray-700 whitespace-nowrap">{{ user.userName }}</td>
              <td class="py-2 text-gray-500">{{ user.departmentNames.join(' / ') }}</td>
            </tr>
          </tbody>
        </table>
        <div v-if="!filteredUsers.length" class="py-8 text-center text-gray-400">
          該当する社員がいません。
        </div>
      </div>
    </div>

    <template #action>
      <WhiteButton text="キャンセル" class="mr-2" @click="cancel" />
      <PrimaryButton
        text="決定"
        :disabled="!checkedUserIds.length"
        :class="checkedUserIds.length ? '' : 'bg-gray-300'"
        @click="decide"
      />
    </template>
  </Modal>
</template>

<script>
import Modal from '@components/Modal.vue'
import PrimaryButton from '@components/PrimaryButton.vue'
import WhiteButton from '@components/WhiteButton.vue'
import * as utils from '@libs/utils'

export default {
  components: {
    Modal,
    PrimaryButton,
    WhiteButton
  },

  props: {
    // モーダルの開閉（親が :open で渡す）
    open: {
      type: Boolean,
      default: false
    },
    // 既に選択済みの社員（再オープン時に復元する）
    selectedUsers: {
      type: Array,
      default: () => []
    },
    // 候補から除外するユーザーID（担当者欄に設定済みの社員）
    // ＊担当者と選択者が重複して二重に登録されるのを防ぐ
    excludeUserId: {
      type: [String, Number],
      default: ''
    }
  },

  data() {
    return {
      // 入力中の検索ワード
      keyword: '',
      // 検索ボタンで確定した検索ワード
      searchWord: '',
      // 絞り込み対象の部署ID
      selectedDepartmentIds: [],
      // チェック済みのユーザーID
      checkedUserIds: [],
      // ユーザー一覧（ユーザー単位に集約したもの）
      users: []
    }
  },

  computed: {
    /**
     * 部署一覧
     */
    departmentList() {
      return this.$store.getters.departmentList || []
    },

    /**
     * 検索条件で絞り込んだユーザー一覧
     */
    filteredUsers() {
      return this.users.filter((user) => {
        // 部署（主・兼任のどちらでもヒットさせる）
        if (this.selectedDepartmentIds.length) {
          const hit = user.departmentIds.some((id) => {
            return this.selectedDepartmentIds.includes(id)
          })
          if (!hit) {
            return false
          }
        }
        // 社員名
        if (this.searchWord && !this.matchName(user, this.searchWord)) {
          return false
        }
        return true
      })
    },

    /**
     * 絞り込み後の全件がチェック済みか
     */
    isAllChecked() {
      if (!this.filteredUsers.length) {
        return false
      }
      return this.filteredUsers.every((user) => {
        return this.checkedUserIds.includes(user.userId)
      })
    }
  },

  watch: {
    open() {
      if (this.open) {
        this.init()
      }
    }
  },

  methods: {
    /**
     * 初期化
     */
    init() {
      this.keyword = ''
      this.searchWord = ''
      this.selectedDepartmentIds = []
      this.users = this.createUserList()
      this.checkedUserIds = (this.selectedUsers || []).map((u) => {
        return u.userId
      })
    },

    /**
     * ユーザー一覧生成
     * userDepartment は「ユーザー × 部署」の行なので、
     * ユーザー単位に集約して兼任部署をまとめる
     */
    createUserList() {
      const userDepartment = this.$store.getters.userDepartment || []
      const map = {}

      for (let i = 0; i < userDepartment.length; i++) {
        const row = userDepartment[i]
        if (!row.userId) {
          continue
        }
        // 担当者欄に設定済みの社員は候補から除外する（二重登録の防止）
        if (this.excludeUserId && String(row.userId) == String(this.excludeUserId)) {
          continue
        }
        if (!map[row.userId]) {
          map[row.userId] = {
            userId: row.userId,
            userName: row.userName,
            departmentIds: [],
            departmentNames: []
          }
        }
        const user = map[row.userId]
        if (!row.departmentId || user.departmentIds.includes(row.departmentId)) {
          continue
        }
        const department = this.departmentList.find((d) => {
          return d.departmentId == row.departmentId
        })
        const name = department ? department.name : ''
        // 主とする部署が判別できる場合は先頭に寄せる
        if (row.main) {
          user.departmentIds.unshift(row.departmentId)
          user.departmentNames.unshift(name)
        } else {
          user.departmentIds.push(row.departmentId)
          user.departmentNames.push(name)
        }
      }

      const list = Object.keys(map).map((key) => {
        return map[key]
      })
      list.sort((a, b) => {
        return String(a.userName).localeCompare(String(b.userName), 'ja')
      })
      return list
    },

    /**
     * 社員名の一致判定
     * @param user 対象ユーザー
     * @param word 検索ワード
     */
    matchName(user, word) {
      const remove = (str) => {
        // 空白を除いて比較する（JavaScriptの \s は全角スペースも対象）
        return String(str || '').replace(/\s/g, '')
      }
      const key = remove(word)
      if (!key) {
        return true
      }
      // 送り仮名を含む表記・除いた表記のどちらでも一致させる
      return remove(user.userName).indexOf(key) > -1 ||
        remove(utils.deleteKana(user.userName)).indexOf(key) > -1
    },

    /**
     * 検索
     */
    search() {
      this.searchWord = this.keyword
    },

    /**
     * 部署の絞り込み切り替え
     * @param departmentId 部署ID
     */
    toggleDepartment(departmentId) {
      const idx = this.selectedDepartmentIds.indexOf(departmentId)
      if (idx > -1) {
        this.selectedDepartmentIds.splice(idx, 1)
      } else {
        this.selectedDepartmentIds.push(departmentId)
      }
    },

    /**
     * ユーザーのチェック切り替え
     * @param userId ユーザーID
     */
    toggleUser(userId) {
      const idx = this.checkedUserIds.indexOf(userId)
      if (idx > -1) {
        this.checkedUserIds.splice(idx, 1)
      } else {
        this.checkedUserIds.push(userId)
      }
    },

    /**
     * すべて選択の切り替え（絞り込み後の一覧が対象）
     */
    toggleAll() {
      if (this.isAllChecked) {
        this.filteredUsers.forEach((user) => {
          const idx = this.checkedUserIds.indexOf(user.userId)
          if (idx > -1) {
            this.checkedUserIds.splice(idx, 1)
          }
        })
      } else {
        this.filteredUsers.forEach((user) => {
          if (!this.checkedUserIds.includes(user.userId)) {
            this.checkedUserIds.push(user.userId)
          }
        })
      }
    },

    /**
     * 決定
     */
    decide() {
      if (!this.checkedUserIds.length) {
        return
      }
      const selected = this.users.filter((user) => {
        return this.checkedUserIds.includes(user.userId)
      })
      this.$emit('decided', utils.clone(selected))
      this.$emit('closed')
    },

    /**
     * キャンセル
     */
    cancel() {
      this.$emit('closed')
    },

    /**
     * 閉じる
     */
    closed() {
      this.keyword = ''
      this.searchWord = ''
      this.selectedDepartmentIds = []
      this.checkedUserIds = []
      this.users = []
    }
  }
}
</script>
